/**
 * Motor de taxas — CalculaMEI "Quanto Sobra de Verdade" (reformulação jun/2026).
 *
 * Base de referência das taxas de maquininhas/adquirentes e marketplaces, com prazos
 * de recebimento. É o ATIVO central do produto: o cálculo do líquido real depende daqui.
 *
 * ⚠️ IMPORTANTE (honestidade do dado): taxas de maquininha são MUITO negociadas por
 * lojista e mudam por plano/promoção; marketplaces variam por categoria/plano. Os valores
 * abaixo são REFERÊNCIAS de plano padrão (sem negociação), jun/2026 — a UI deixa o usuário
 * ajustar a taxa do caso dele. Roadmap: rotina de atualização periódica (agente) com revisão.
 */

export const TAXAS_ATUALIZADAS_EM = 'jun/2026'
export const TAXAS_DISCLAIMER =
  'Taxas de referência (plano padrão, jun/2026). A sua pode ser diferente por negociação ou plano — dá pra ajustar.'

export type ModalidadePagamento = 'pix' | 'debito' | 'credito_avista' | 'credito_parcelado'

export interface Maquininha {
  id: string
  nome: string
  /** % descontado por modalidade (sobre o valor da venda). */
  pix: number
  debito: number
  credito_avista: number
  /** crédito parcelado 2 a 6x — % total descontado da venda. */
  credito_2a6: number
  /** crédito parcelado 7 a 12x — % total descontado da venda. */
  credito_7a12: number
  /** Dias até o crédito cair SEM antecipar (recebimento padrão). */
  recebimentoCreditoDias: number
  /** Custo % ao mês para antecipar e receber em ~1 dia. 0 = já cai na hora. */
  antecipacaoMensalPct: number
}

export interface Marketplace {
  id: string
  nome: string
  /** Comissão % sobre a venda. */
  comissaoPct: number
  /** Taxa de transação/financeira adicional %. */
  taxaTransacaoPct: number
  /** Taxa fixa em R$ por pedido/item (0 se não houver). */
  taxaFixaReais: number
  /** Dias até o repasse cair (após confirmação/entrega). */
  recebimentoDias: number
  obs?: string
}

/**
 * Maquininhas — valores de referência de plano padrão (jun/2026).
 * Fontes: páginas públicas de tarifas das adquirentes. Editável pelo usuário.
 */
export const MAQUININHAS: Maquininha[] = [
  {
    id: 'mercadopago',
    nome: 'Mercado Pago (Point)',
    pix: 0.49,
    debito: 1.99,
    credito_avista: 4.98,
    credito_2a6: 5.31,
    credito_7a12: 5.61,
    recebimentoCreditoDias: 14,
    antecipacaoMensalPct: 1.99,
  },
  {
    id: 'pagbank',
    nome: 'PagBank (PagSeguro)',
    pix: 0.4,
    debito: 1.49,
    credito_avista: 4.39,
    credito_2a6: 5.59,
    credito_7a12: 6.39,
    recebimentoCreditoDias: 30,
    antecipacaoMensalPct: 2.0,
  },
  {
    id: 'stone',
    nome: 'Stone',
    pix: 0.99,
    debito: 1.39,
    credito_avista: 3.19,
    credito_2a6: 3.79,
    credito_7a12: 4.19,
    recebimentoCreditoDias: 30,
    antecipacaoMensalPct: 2.1,
  },
  {
    id: 'cielo',
    nome: 'Cielo',
    pix: 0.0,
    debito: 1.39,
    credito_avista: 3.09,
    credito_2a6: 3.79,
    credito_7a12: 4.39,
    recebimentoCreditoDias: 30,
    antecipacaoMensalPct: 2.5,
  },
  {
    id: 'infinitepay',
    nome: 'InfinitePay',
    pix: 0.0,
    debito: 1.37,
    credito_avista: 3.15,
    credito_2a6: 6.39,
    credito_7a12: 10.99,
    recebimentoCreditoDias: 1,
    antecipacaoMensalPct: 0, // já cai na hora (taxa "na hora" embutida acima)
  },
  {
    id: 'sumup',
    nome: 'SumUp',
    pix: 0.0,
    debito: 1.9,
    credito_avista: 3.45,
    credito_2a6: 4.6,
    credito_7a12: 5.9,
    recebimentoCreditoDias: 30,
    antecipacaoMensalPct: 2.99,
  },
]

/**
 * Marketplaces / apps — comissões de referência (jun/2026). Editável pelo usuário.
 * iFood tem dois modos: entrega própria (comissão menor) vs logística do iFood.
 */
export const MARKETPLACES: Marketplace[] = [
  {
    id: 'ifood_proprio',
    nome: 'iFood (entrega própria)',
    comissaoPct: 12,
    taxaTransacaoPct: 3.5,
    taxaFixaReais: 0,
    recebimentoDias: 30,
    obs: 'Comissão sobre entrega feita por você + taxa de pagamento online.',
  },
  {
    id: 'ifood_entrega',
    nome: 'iFood (logística iFood)',
    comissaoPct: 23,
    taxaTransacaoPct: 3.5,
    taxaFixaReais: 0,
    recebimentoDias: 30,
    obs: 'Comissão maior quando a entrega é feita pelo iFood.',
  },
  {
    id: 'mercadolivre',
    nome: 'Mercado Livre (Clássico)',
    comissaoPct: 12,
    taxaTransacaoPct: 0,
    taxaFixaReais: 6,
    recebimentoDias: 14,
    obs: 'Taxa fixa por unidade em itens abaixo de R$ 79. Repasse após entrega.',
  },
  {
    id: 'mercadolivre_premium',
    nome: 'Mercado Livre (Premium)',
    comissaoPct: 17,
    taxaTransacaoPct: 0,
    taxaFixaReais: 6,
    recebimentoDias: 14,
    obs: 'Anúncio Premium (parcelamento sem juros pro comprador).',
  },
  {
    id: 'shopee',
    nome: 'Shopee',
    comissaoPct: 20,
    taxaTransacaoPct: 2,
    taxaFixaReais: 0,
    recebimentoDias: 7,
    obs: 'Comissão com programa de frete grátis + taxa de transação. Teto de comissão por item.',
  },
]

export function getMaquininha(id: string): Maquininha | undefined {
  return MAQUININHAS.find((m) => m.id === id)
}

export function getMarketplace(id: string): Marketplace | undefined {
  return MARKETPLACES.find((m) => m.id === id)
}
